import type { Editor } from "@tiptap/react";

/**
 * Attributes applied to every anchor the editor produces.
 *
 * - `class` keeps a link visually distinct even outside a `.prose` wrapper; the
 *   canonical color lives in `.prose a` (globals.css). Harmless in sent email
 *   HTML — clients ignore unknown classes and fall back to UA blue.
 * - `target`/`rel` open links safely in a new tab (no `window.opener` leak).
 */
export const LINK_HTML_ATTRIBUTES = {
  class: "text-primary underline underline-offset-2",
  target: "_blank",
  rel: "noopener noreferrer",
} as const;

/**
 * Add or update the link on the current selection. `extendMarkRange` makes the
 * change apply to the whole link even when the cursor merely sits inside it
 * (no explicit selection). `href` must already be sanitized — see
 * {@link ./link-url.sanitizeLinkHref}.
 */
export function setEditorLink(editor: Editor, href: string): void {
  editor
    .chain()
    .focus()
    .extendMarkRange("link")
    .setLink({
      href,
      target: LINK_HTML_ATTRIBUTES.target,
      rel: LINK_HTML_ATTRIBUTES.rel,
    })
    .run();
}

/** Remove the link mark from the current selection (or the link under the cursor). */
export function removeEditorLink(editor: Editor): void {
  editor.chain().focus().extendMarkRange("link").unsetLink().run();
}

/** Read the href of the link under the cursor, or `""` when none. */
export function getActiveLinkHref(editor: Editor): string {
  const href = editor.getAttributes("link").href;
  return typeof href === "string" ? href : "";
}
